import { useCallback } from 'react';
import { useSignalR } from '../hooks';
import { useSceneEffectsSync } from './useSceneEffectsSync';
import { executeAiAssistantActions, type AiActionResult } from '../util/aiActionExecutor';
import type { ParsedAssistantResponse } from '../util/aiResponseParser';
import { useAuthStore, useEditorStore, useMovieStore } from '../stores';
import { mapLibraryObjectResponses } from '../util/mapLibraryObjectResponses';

export function useAiActionExecutor() {
  const { sceneObjectPatchStateProperties, connect, status: signalRStatus, libraryObjects } =
    useSignalR(false);
  const { applyEffectPayload } = useSceneEffectsSync();
  const selectedMovieId = useMovieStore((state) => state.selectedMovieId);
  const currentScene = useMovieStore((state) => state.currentScene);
  const currentMovieResponse = useMovieStore((state) => state.currentMovieResponse);
  const currentFrame = useEditorStore((state) => state.currentFrame);
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const addLibraryItem = useMovieStore((state) => state.addLibraryItem);
  const setLibrary = useMovieStore((state) => state.setLibrary);

  const refreshLibrary = useCallback(async () => {
    if (!currentMovieResponse || !userId) return;
    if (signalRStatus !== 'Connected') {
      await connect();
    }
    const library = await libraryObjects(currentMovieResponse);
    setLibrary(mapLibraryObjectResponses(library, userId, currentMovieResponse.id));
  }, [connect, currentMovieResponse, libraryObjects, setLibrary, signalRStatus, userId]);

  const executeActions = useCallback(
    async (parsed: ParsedAssistantResponse): Promise<AiActionResult[]> => {
      if (
        parsed.payloads.length === 0 &&
        parsed.actions.length === 0 &&
        parsed.jobs.length === 0
      ) {
        return [];
      }

      const sceneId = currentScene?.id ? parseInt(currentScene.id, 10) : null;

      return executeAiAssistantActions(parsed, {
        movieId: selectedMovieId ?? 0,
        sceneId: Number.isFinite(sceneId) ? sceneId : null,
        currentFrame,
        userId,
        sceneObjectPatchStateProperties,
        connect,
        signalRStatus,
        applyEffectPayload,
        onLibraryItemCreated: addLibraryItem,
        refreshLibrary,
      });
    },
    [
      selectedMovieId,
      currentScene?.id,
      currentFrame,
      userId,
      sceneObjectPatchStateProperties,
      connect,
      signalRStatus,
      applyEffectPayload,
      addLibraryItem,
      refreshLibrary,
    ]
  );

  return { executeActions };
}
